/** Default search radius in kilometers (100 miles) */
export const DEFAULT_MILITARY_SEARCH_RADIUS_KM = 160.934;

/**
 * Generates boundary coordinates around a point with a specified radius in kilometers
 */
export function getBoundingBoxCoordinates(lat: number, lon: number, radiusKm: number = DEFAULT_MILITARY_SEARCH_RADIUS_KM): { boundaryTopLeft: number[]; boundaryBottomRight: number[] } {
  const latRad = lat * Math.PI / 180;
  const lonRad = lon * Math.PI / 180;
  const radiusEarth = 6371; // Earth's radius in kilometers
  const radiusRad = radiusKm / radiusEarth;

  const minLat = latRad - radiusRad;
  const maxLat = latRad + radiusRad;
  const minLon = lonRad - radiusRad / Math.cos(latRad);
  const maxLon = lonRad + radiusRad / Math.cos(latRad);

  // Convert back to degrees
  const minLatDeg = minLat * 180 / Math.PI;
  const maxLatDeg = maxLat * 180 / Math.PI;
  const minLonDeg = minLon * 180 / Math.PI;
  const maxLonDeg = maxLon * 180 / Math.PI;

  return {
    boundaryTopLeft: [minLonDeg, maxLatDeg],
    boundaryBottomRight: [maxLonDeg, minLatDeg],
  };
}

/**
 * Generates a boundary from a WKT point string
 * @param wktPoint The WKT point string in format "POINT(lon lat)"
 * @param radiusKm The radius in kilometers for the boundary (default: 100 miles)
 * @returns An object with boundaryTopLeft and boundaryBottomRight arrays, or undefined if WKT is invalid
 */
export function generateBoundaryFromWkt(wktPoint: string, radiusKm: number = DEFAULT_MILITARY_SEARCH_RADIUS_KM): { boundaryTopLeft: number[]; boundaryBottomRight: number[] } | undefined {
  const match = wktPoint.match(/POINT\(([-\d.]+) ([-\d.]+)\)/);
  if (match) {
    const [, lon, lat] = match;
    return getBoundingBoxCoordinates(parseFloat(lat), parseFloat(lon), radiusKm);
  }
  return undefined;
}
